import React, {
    createContext,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'

export type ThemeMode = 'light' | 'dark'

interface ThemeModeInfo {
    mode: ThemeMode
    setMode(mode: ThemeMode): void
    toggleMode(): void
}

const ThemeModeDefault: ThemeModeInfo = {
    mode: 'light',
    setMode: () => {},
    toggleMode: () => {},
}

const ThemeModeContext = createContext<ThemeModeInfo>(ThemeModeDefault)

export function useThemeMode() {
    return useContext(ThemeModeContext)
}

const THEME_MODE_KEY = 'themeMode'

export function ThemeModeProvider(props: { children: React.ReactNode }) {
    const [mode, setMode] = useState<ThemeMode>(() => {
        const saved = localStorage.getItem(THEME_MODE_KEY)
        if (saved === 'dark' || saved === 'light') return saved
        return 'light'
    })

    useEffect(() => {
        localStorage.setItem(THEME_MODE_KEY, mode)
        document.documentElement.setAttribute('data-theme', mode)
    }, [mode])

    useEffect(() => {
        const restoreSaved = () => {
            const saved = localStorage.getItem(THEME_MODE_KEY)
            setMode(saved === 'dark' ? 'dark' : 'light')
        }
        const onBeforePrint = () => setMode('light')
        const onAfterPrint = restoreSaved
        const printQuery = window.matchMedia('print')
        const onPrintChange = (e: MediaQueryListEvent) => {
            if (e.matches) setMode('light')
            else restoreSaved()
        }

        window.addEventListener('beforeprint', onBeforePrint)
        window.addEventListener('afterprint', onAfterPrint)
        if (printQuery.addEventListener) {
            printQuery.addEventListener('change', onPrintChange)
        } else {
            ;(printQuery as any).addListener?.(onPrintChange)
        }

        return () => {
            window.removeEventListener('beforeprint', onBeforePrint)
            window.removeEventListener('afterprint', onAfterPrint)
            if (printQuery.removeEventListener) {
                printQuery.removeEventListener('change', onPrintChange)
            } else {
                ;(printQuery as any).removeListener?.(onPrintChange)
            }
        }
    }, [])

    const handleSetMode = useCallback((next: ThemeMode) => setMode(next), [])

    const toggleMode = useCallback(
        () => setMode((prev) => (prev === 'dark' ? 'light' : 'dark')),
        []
    )

    const value = useMemo(
        () => ({ mode, setMode: handleSetMode, toggleMode }),
        [mode, handleSetMode, toggleMode]
    )

    return (
        <ThemeModeContext.Provider value={value}>
            {props.children}
        </ThemeModeContext.Provider>
    )
}

export default ThemeModeContext
